import styled from "styled-components";
import { siteDarkBlue, siteDarkGreen } from "../../providers/Theme";
import {
  bodyFontBase,
  bodyFontMd,
  buttonFontSize
} from "../shared.styled.components";
import PinkGradient from "../../assets/faded-pink-gradient.png";

export const HeroSection = styled.section`
  position: relative;
  z-index: 0;
  padding: 4rem 2rem 4.5rem;
  text-align: center;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: url(${PinkGradient});
    background-size: cover;
    background-position: center;
    z-index: 0;
  }
`;

export const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 52rem;
  margin: 0 auto;
`;

export const HeroHeading = styled.h1`
  color: ${siteDarkBlue};
  font-weight: 800;
  font-size: 2.2rem;
  margin-bottom: 1rem;

  ${({ theme }) => theme.md`
    font-size: 2.8rem;
  `}
`;

export const HeroSubtitle = styled.p`
  color: ${siteDarkBlue};
  font-size: ${bodyFontBase};
  line-height: 1.7;
  margin: 0;

  ${({ theme }) => theme.md`
    font-size: ${bodyFontMd};
  `}
`;

// ── Connect With Us ───────────────────────────────────────────────────────────

export const ConnectSection = styled.section`
  background: white;
  padding: 3.5rem 2rem 4rem;
`;

export const ConnectInner = styled.div`
  max-width: 72rem;
  margin: 0 auto;
`;

export const ConnectHeading = styled.h2`
  color: ${siteDarkBlue};
  font-weight: 800;
  font-size: 2rem;
  margin-bottom: 0.75rem;
`;

export const ConnectSubtitle = styled.p`
  color: #555;
  font-size: ${bodyFontBase};
  line-height: 1.7;
  margin-bottom: 2rem;
`;

export const ConnectSubHeading = styled.h3`
  color: ${siteDarkBlue};
  font-weight: 700;
  font-size: 1.25rem;
  margin-bottom: 0.4rem;
`;

export const ConnectBodyText = styled.p`
  color: #555;
  font-size: ${bodyFontBase};
  line-height: 1.6;
  margin-bottom: 0.75rem;
`;

export const EmailRow = styled.a`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: ${siteDarkGreen};
  font-weight: 600;
  font-size: ${bodyFontBase};
  text-decoration: none;
  margin-bottom: 2rem;

  &:hover {
    text-decoration: underline;
    color: ${siteDarkGreen};
  }
`;

export const SocialRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;

export const SupportRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  ${({ theme }) => theme.md`
    flex-direction: row;
    align-items: center;
    gap: 2rem;

    & > *:first-child {
      flex: 1;
    }

    & > *:last-child {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `}
`;

export const AddressBlock = styled.div`
  color: #555;
  font-size: ${bodyFontBase};
  line-height: 1.7;
  white-space: nowrap;

  strong {
    color: ${siteDarkBlue};
  }
`;

// ── Team Cards ────────────────────────────────────────────────────────────────

export const TeamCardsSection = styled.section`
  background: white;
  padding: 3rem 2rem 4rem;

  ${({ theme }) => theme.lg`
    padding: 4rem 4rem 5rem;
  `}
`;

export const TeamCardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  max-width: 72rem;
  margin: 0 auto;

  ${({ theme }) => theme.md`
    grid-template-columns: repeat(2, 1fr);

    & > *:last-child:nth-child(odd) {
      grid-column: 1 / -1;
      max-width: calc(50% - 0.75rem);
      margin: 0 auto;
      width: 100%;
    }
  `}

  ${({ theme }) => theme.xl`
    grid-template-columns: repeat(3, 1fr);

    & > *:last-child:nth-child(odd) {
      grid-column: auto;
      max-width: none;
      margin: 0;
    }
  `}
`;

export const TeamCard = styled.div<{ $color: string }>`
  background: #f4f6fb;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const TeamCardAccent = styled.div<{ $color: string }>`
  background: ${({ $color }) => $color};
  height: 10px;
  width: 100%;
`;

export const TeamCardBody = styled.div`
  padding: 1.75rem 1.75rem 2rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  text-align: center;
`;

export const TeamCardHeading = styled.h2<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-weight: 800;
  font-size: 2rem;
  margin-bottom: 0.85rem;
  line-height: 1.2;
`;

export const TeamCardText = styled.p`
  color: #555;
  font-size: ${bodyFontBase};
  line-height: 1.65;
  flex: 1;
  margin-bottom: 1.5rem;
`;

export const TeamCardButton = styled.a<{ $color: string }>`
  display: block;
  background: ${({ $color }) => $color};
  color: white;
  font-weight: 700;
  font-size: ${buttonFontSize};
  text-align: center;
  text-decoration: none;
  border-radius: 0.6rem;
  padding: 0.75rem 1.5rem;
  transition: filter 0.15s ease;
  cursor: pointer;

  &:hover {
    filter: brightness(0.9);
    color: white;
  }
`;
